/**
 * Which of the two comped breakdown layouts to render.
 *
 * The distinction is presentational ONLY -- the arithmetic is identical in both. See
 * modalTotalDueOf(): base amount + required fees + transportation, whichever pattern is chosen.
 * What differs is whether the intermediate "base + required fees" figure is shown as a bold
 * headline with its components decomposed beneath it (DECOMPOSED), or omitted because the buyer
 * supplied the base amount themselves and would not recognise a system-composed figure (ADDITIVE).
 *
 * Keeping the arithmetic common is what stops this component becoming a switchboard; if you find
 * yourself adding a second `if (pattern === ...)` around a number rather than around markup, the
 * split is in the wrong place.
 *
 * SCREAMING_SNAKE = 'camelCase' matches DripTotalLabelContext and the wider codebase precedent, and
 * an enum lets a host write an exhaustive Record over the two patterns.
 */
export enum ModalPriceBreakdownPattern {
  /** Pattern A in the work-items doc. The system presents an already-all-in amount: Buy Now, Offer Sent. */
  DECOMPOSED = 'decomposed',
  /** Pattern B. The buyer types the amount or the seller offered it: Bid, Auto Bid, Make Offer, Counter. */
  ADDITIVE = 'additive'
}

/**
 * One fee line, with its label ALREADY TRANSLATED.
 *
 * Deliberately not DripFeeRow, which carries a raw backend `description` such as 'DOCUMENT_PROC_FEE'.
 * This component renders what it is given verbatim, so handing it a DripFeeRow would print a backend
 * enum to a buyer. Hosts translate through FeeToolTipService.feeTranslations -- the same map
 * getDripFeeValues uses -- so a fee reads identically in the tooltip and in the modal.
 */
export interface ModalPriceBreakdownRow {
  label: string;
  amount: number;
}

/**
 * Every string this component renders. No literal copy lives in the template: the component is
 * presentational and injects no TranslocoService, so the host resolves the M1.4 keys and passes them
 * down. This is the translation-object-as-input convention already used across RUIC and webcore.
 *
 * labelBaseAmount is the only caller-specific one -- 'BUY NOW PRICE', 'BID AMOUNT',
 * 'MAX AUTO BID AMOUNT', 'COUNTER OFFER' -- which is why the pattern alone cannot imply it.
 */
export interface ModalPriceBreakdownTranslations {
  /** Labels the base amount: the bold headline in DECOMPOSED, the bare/input row in ADDITIVE. */
  labelBaseAmount: string;
  /** Sub-row label under a DECOMPOSED headline. Unused by ADDITIVE, which has no sub-rows. */
  labelVehiclePrice: string;
  /** The transportation row. Rendered only when transportation applies. */
  labelTransportationFee: string;
  /**
   * The highlighted total's label.
   *
   * Passed in, and read by hosts from a key of its OWN (shared.dripPricing.labelModalTotalDue)
   * rather than from labelTotalPurchasePrice, because the two name different numbers: the list
   * surfaces exclude transportation, this total includes it. The words are currently identical and
   * the collision is unresolved; keeping this a separate passed-in key means settling it is a copy
   * change in two bundles, not a code change in nine modals.
   */
  labelTotalDue: string;
  /**
   * What the total shows when there is no base amount yet -- '$--' in en, '-- $' in fr.
   *
   * A key rather than a literal because the symbol's side depends on locale, and because a literal
   * '$' would fail M4.1's "no literal currency symbol" criterion.
   */
  textTotalPlaceholder: string;
  /**
   * Screen-reader name for the group of sub-rows beneath a DECOMPOSED headline, so the list is
   * announced as subordinate to the amount it decomposes rather than as a peer of it.
   */
  ariaLabelPriceComponents: string;
}
